import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import StudentLayout from "@/components/StudentLayout";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { BookOpen, ListChecks, Layers, Sparkles, ArrowRight, Languages, Users, ClipboardList, Search, Gauge, Flame } from "lucide-react";
import { Input } from "@/components/ui/input";
import { AiServiceNotice } from "@/components/AiServiceNotice";
import { MarkdownMessage } from "@/components/MarkdownMessage";
import { toast } from "sonner";

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

const SCOPES = [
  { key: "this", label: "This lesson only", desc: "Test you only on the selected lesson.", icon: BookOpen },
  { key: "multi", label: "Multiple lessons", desc: "Pick the lessons you want included.", icon: Layers },
  { key: "all", label: "All lessons", desc: "Full subject assessment across every lesson.", icon: Sparkles },
];

// Feb 2026: two strictly-separated difficulty modes. Easy pulls questions
// verbatim from each lesson's LBA PDF; Medium pulls verbatim from the MBA PDF.
// The two banks are NEVER mixed and the backend blocks generation with a
// clear message when the required PDF isn't uploaded for a selected lesson.
const DIFFICULTIES = [
  { key: "easy", label: "Easy", desc: "Questions from the Easy PDF (LBA) for each lesson.", icon: Gauge },
  { key: "medium", label: "Medium", desc: "Questions from the Medium PDF (MBA) for each lesson.", icon: Flame },
];

export default function StudentTuitions() {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [lessonId, setLessonId] = useState("");
  const [loadingSubs, setLoadingSubs] = useState(true);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scope, setScope] = useState("this");
  const [difficulty, setDifficulty] = useState("easy");   // "easy" | "medium"
  const [multiSelected, setMultiSelected] = useState([]);
  const [langMode, setLangMode] = useState("kanglish");
  const [langSaving, setLangSaving] = useState(false);
  const [peers, setPeers] = useState([]);
  const [history, setHistory] = useState([]);
  const [notesOpen, setNotesOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/tuition/assessment/history");
        if (!cancelled) setHistory(data.attempts || []);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/tuition/study-together").then(r => { if (!cancelled) setPeers(r.data?.peers || []); }).catch(() => {});
    load();
    const id = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/student/me");
        if (data?.language_mode) setLangMode(data.language_mode);
      } catch { /* keep default */ }
    })();
  }, []);

  const changeLang = async (mode) => {
    setLangSaving(true);
    try {
      await api.patch("/student/me/language-mode", { language_mode: mode });
      setLangMode(mode);
      toast.success(mode === "kanglish" ? "Switched to Kanglish (Kannada + English)" : "Switched to English");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not update");
    } finally { setLangSaving(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/tuition/subjects");
        if (!cancelled) setSubjects(data);
      } catch (e) {
        if (!cancelled) toast.error(apiErr(e.response?.data?.detail) || "Could not load subjects");
      } finally { if (!cancelled) setLoadingSubs(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!subjectId) { setLessons([]); setLessonId(""); return undefined; }
    let cancelled = false;
    setLoadingLessons(true); setLessonId(""); setMultiSelected([]);
    (async () => {
      try {
        const { data } = await api.get("/tuition/lessons", { params: { subject_id: subjectId } });
        if (!cancelled) setLessons(data);
      } catch (e) {
        if (!cancelled) toast.error(apiErr(e.response?.data?.detail) || "Could not load lessons");
      } finally { if (!cancelled) setLoadingLessons(false); }
    })();
    return () => { cancelled = true; };
  }, [subjectId]);

  const selectedLesson = useMemo(() => lessons.find((l) => l.id === lessonId) || null, [lessons, lessonId]);
  const subjectName = useMemo(() => subjects.find((s) => s.id === subjectId)?.name || "", [subjects, subjectId]);
  const lessonTitle = selectedLesson?.title || "";
  const lessonReady = selectedLesson?.indexed !== false; // server may omit field for older lessons
  const startDisabled = !subjectId || !lessonId || !lessonReady;

  const [generatingAssess, setGeneratingAssess] = useState(false);
  const [assessError, setAssessError] = useState(null);
  const startAssessment = async () => {
    // Resolve which lessons to assess based on scope.
    let lesson_ids = [];
    if (scope === "this") lesson_ids = lessonId ? [lessonId] : [];
    else if (scope === "multi") lesson_ids = multiSelected;
    else lesson_ids = lessons.map((l) => l.id);
    if (!lesson_ids.length) { toast.error("Pick at least one lesson first."); return; }
    setGeneratingAssess(true);
    setAssessError(null);
    try {
      const { data } = await api.post("/tuition/assessment/generate", {
        lesson_ids, n_questions: 15, difficulty,
      });
      // Stash for the assessment screen (it doesn't refetch — questions are one-shot).
      try {
        sessionStorage.setItem(`tassess:${data.session_id}`, JSON.stringify(data));
      } catch { /* private mode etc. — page will show the empty-state */ }
      setDialogOpen(false);
      navigate(`/student/tuitions/assessment/${data.session_id}`);
    } catch (err) {
      setAssessError(err);
    } finally {
      setGeneratingAssess(false);
    }
  };

  const toggleMulti = (id) => setMultiSelected((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  return (
    <StudentLayout>
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500 mb-2">Tuitions</div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Learn at your own pace</h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">Pick a subject and lesson to start, or jump straight to an assessment.</p>
          </div>
          <div className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full p-1" data-testid="lang-mode-toggle">
            <Languages className="h-4 w-4 ml-2 text-slate-400" />
            <button
              type="button"
              disabled={langSaving}
              onClick={() => changeLang("kanglish")}
              className={`text-xs px-3 py-1.5 rounded-full transition-colors ${langMode === "kanglish" ? "bg-sky-500 text-white" : "text-slate-500 hover:text-slate-900 dark:hover:text-slate-100"}`}
              data-testid="lang-mode-kanglish"
            >ಕನ್ನಡ + English</button>
            <button
              type="button"
              disabled={langSaving}
              onClick={() => changeLang("english")}
              className={`text-xs px-3 py-1.5 rounded-full transition-colors ${langMode === "english" ? "bg-sky-500 text-white" : "text-slate-500 hover:text-slate-900 dark:hover:text-slate-100"}`}
              data-testid="lang-mode-english"
            >English</button>
          </div>
          <Button variant="outline" size="sm" className="rounded-full h-9" onClick={() => setSearchOpen(true)} data-testid="tuition-search-notes-btn">
            <Search className="h-4 w-4 mr-2" /> Search notes
          </Button>
        </div>

        {searchOpen && <NotesSearchModal onClose={() => setSearchOpen(false)} />}

        {peers.length > 0 && (
          <div className="mb-5 rounded-3xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/70 dark:bg-emerald-950/30 p-5" data-testid="study-together-card">
            <div className="flex items-center gap-2 mb-3">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
              </span>
              <Users className="h-4 w-4 text-emerald-700 dark:text-emerald-300" />
              <span className="text-sm font-semibold text-emerald-900 dark:text-emerald-100" data-testid="study-together-title">
                {peers.length} classmate{peers.length === 1 ? "" : "s"} studying with you right now
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {peers.slice(0, 8).map((p) => {
                const canJoin = p.subject_id && p.lesson_id;
                return (
                  <button
                    key={p.student_id}
                    type="button"
                    disabled={!canJoin}
                    onClick={() => canJoin && navigate(`/student/tuitions/${p.subject_id}/${p.lesson_id}/chat`)}
                    className="group flex items-center gap-2 rounded-full bg-white dark:bg-slate-800 border border-emerald-100 dark:border-emerald-900 pl-1.5 pr-2 py-1.5 enabled:hover:border-emerald-400 enabled:hover:shadow-sm transition disabled:opacity-70 disabled:cursor-default"
                    data-testid={`study-peer-${p.student_id}`}
                    title={canJoin ? `Join ${p.name?.split(" ")[0]} in ${p.subject}${p.lesson ? " · " + p.lesson : ""}` : `${p.name} · ${p.subject}`}
                  >
                    <span className="h-7 w-7 rounded-full bg-emerald-500 text-white text-xs font-semibold flex items-center justify-center shrink-0">
                      {(p.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}
                    </span>
                    <span className="text-xs text-slate-700 dark:text-slate-200 leading-tight text-left">
                      <span className="font-medium">{p.name?.split(" ")[0] || "Classmate"}</span>
                      {p.subject ? <span className="text-slate-400"> · {p.subject}</span> : null}
                    </span>
                    {canJoin && (
                      <span className="ml-0.5 inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300 opacity-70 group-hover:opacity-100">
                        Join <ArrowRight className="h-3 w-3" />
                      </span>
                    )}
                  </button>
                );
              })}
              {peers.length > 8 && (
                <div className="flex items-center px-3 text-xs text-emerald-800 dark:text-emerald-200">+{peers.length - 8} more</div>
              )}
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl p-6 sm:p-8" data-testid="tuition-card">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-2 block">Subject</Label>
              <Select value={subjectId} onValueChange={setSubjectId} disabled={loadingSubs}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="tuition-subject-select"><SelectValue placeholder={loadingSubs ? "Loading…" : "Select a subject"} /></SelectTrigger>
                <SelectContent>
                  {subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-2 block">Lesson</Label>
              <Select value={lessonId} onValueChange={setLessonId} disabled={!subjectId || loadingLessons}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="tuition-lesson-select">
                  <SelectValue placeholder={!subjectId ? "Pick a subject first" : loadingLessons ? "Loading…" : "Select a lesson"} />
                </SelectTrigger>
                <SelectContent>
                  {lessons.map((l) => (
                    <SelectItem
                      key={l.id}
                      value={l.id}
                      disabled={l.indexed === false}
                      data-testid={`tuition-lesson-opt-${l.id}`}
                    >
                      {l.title}
                      {l.has_class_notes && (
                        <span className="ml-2 text-xs text-emerald-600 dark:text-emerald-400 font-medium">· Notes</span>
                      )}
                      {l.indexed === false && (
                        <span className="ml-2 text-xs text-slate-400">· not ready</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <Button
              size="lg"
              disabled={startDisabled}
              onClick={() => navigate(`/student/tuitions/${subjectId}/${lessonId}/chat`)}
              data-testid="tuition-start-lesson-btn"
              className="rounded-full bg-sky-500 hover:bg-sky-600"
            >
              <BookOpen className="h-4 w-4 mr-2" /> Start lesson
            </Button>
            <Button
              size="lg"
              variant="outline"
              disabled={!subjectId}
              onClick={() => { setScope(lessonId ? "this" : "all"); setDialogOpen(true); }}
              data-testid="tuition-assessment-btn"
              className="rounded-full"
            >
              <ListChecks className="h-4 w-4 mr-2" /> Take assessment <ArrowRight className="h-4 w-4 ml-2 opacity-60" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              disabled={!lessonId}
              onClick={() => setNotesOpen(true)}
              data-testid="tuition-class-notes-btn"
              className="rounded-full"
            >
              <ClipboardList className="h-4 w-4 mr-2" /> Class Notes
            </Button>
          </div>
        </div>

        {notesOpen && (
          <StudentClassNotesModal
            lessonId={lessonId}
            lessonTitle={lessons.find((l) => l.id === lessonId)?.title || "Lesson"}
            onClose={() => setNotesOpen(false)}
          />
        )}

        {history.length > 0 && (
          <div className="mt-6 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl p-6 sm:p-7" data-testid="tuition-history-card">
            <div className="flex items-center gap-2 mb-4">
              <ClipboardList className="h-4 w-4 text-sky-500" />
              <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Your practice tests</h2>
            </div>
            <div className="space-y-2">
              {history.map((a) => {
                const pct = a.max_score ? Math.round((a.score / a.max_score) * 100) : 0;
                const good = pct >= 60;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => navigate(`/student/tuitions/assessment/review/${a.id}`)}
                    className="w-full flex items-center gap-3 text-left rounded-2xl border border-slate-200 dark:border-slate-700 px-4 py-3 hover:border-sky-300 hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
                    data-testid={`tuition-history-item-${a.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                        {a.subject_name}
                        <span className="text-slate-400 font-normal"> · {(a.lessons_meta || []).map((l) => l.title).join(", ") || "All lessons"}</span>
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">{fmtWhen(a.created_at)}</div>
                    </div>
                    <span className={`text-sm font-semibold px-2.5 py-1 rounded-full ${good ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-amber-50 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300"}`}>
                      {a.score}/{a.max_score}
                    </span>
                    <ArrowRight className="h-4 w-4 text-slate-300 shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="tuition-assessment-dialog">
          <DialogHeader>
            <DialogTitle style={{fontFamily:"Outfit"}}>Pick an assessment scope</DialogTitle>
            <DialogDescription>
              {subjectName ? <>For <span className="font-semibold text-slate-700 dark:text-slate-200">{subjectName}</span></> : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            {SCOPES.map((s) => {
              const disabled = s.key === "this" && !lessonId;
              return (
                <button
                  key={s.key}
                  type="button"
                  disabled={disabled}
                  onClick={() => setScope(s.key)}
                  data-testid={`scope-${s.key}`}
                  className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                    scope === s.key
                      ? "border-sky-300 bg-sky-50 dark:bg-sky-900/30 dark:border-sky-700"
                      : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900"
                  } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <div className="h-9 w-9 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center shrink-0">
                    <s.icon className="h-4 w-4 text-sky-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{s.label}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{s.desc}{disabled ? " · pick a lesson first" : ""}</div>
                  </div>
                  <div className={`h-4 w-4 rounded-full border-2 mt-1.5 ${scope === s.key ? "border-sky-500 bg-sky-500" : "border-slate-300 dark:border-slate-600"}`} />
                </button>
              );
            })}
          </div>

          {scope === "multi" && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 max-h-56 overflow-y-auto space-y-1.5" data-testid="multi-lesson-picker">
              {lessons.length === 0 ? (
                <div className="text-xs text-slate-400">No lessons.</div>
              ) : lessons.map((l) => {
                const notReady = l.indexed === false;
                return (
                  <label key={l.id} className={`flex items-center gap-2 text-sm ${notReady ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                    <Checkbox
                      checked={multiSelected.includes(l.id)}
                      onCheckedChange={() => !notReady && toggleMulti(l.id)}
                      disabled={notReady}
                      data-testid={`multi-lesson-${l.id}`}
                    />
                    {l.title}
                    {notReady && <span className="ml-1 text-xs text-slate-400">· not ready</span>}
                  </label>
                );
              })}
            </div>
          )}

          {/* Difficulty picker — questions come STRICTLY from the matching
              PDF (Easy=LBA, Medium=MBA). If the required PDF is missing on
              any selected lesson, the API returns 409 with a clear message. */}
          <div className="mt-1">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5" data-testid="difficulty-label">
              Difficulty
            </div>
            <div className="grid grid-cols-2 gap-2" data-testid="difficulty-picker">
              {DIFFICULTIES.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => setDifficulty(d.key)}
                  data-testid={`difficulty-${d.key}`}
                  className={`text-left flex items-start gap-2 p-2.5 rounded-xl border transition-colors ${
                    difficulty === d.key
                      ? "border-sky-300 bg-sky-50 dark:bg-sky-900/30 dark:border-sky-700"
                      : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900"
                  }`}
                >
                  <div className="h-7 w-7 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center shrink-0">
                    <d.icon className="h-3.5 w-3.5 text-sky-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{d.label}</div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug">{d.desc}</div>
                  </div>
                  <div className={`h-3.5 w-3.5 rounded-full border-2 mt-1 shrink-0 ${difficulty === d.key ? "border-sky-500 bg-sky-500" : "border-slate-300 dark:border-slate-600"}`} />
                </button>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={startAssessment}
              disabled={generatingAssess || (scope === "multi" && multiSelected.length === 0)}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="tuition-assessment-start-btn"
            >
              {generatingAssess ? "Generating…" : (<>Start assessment <ArrowRight className="h-4 w-4 ml-1" /></>)}
            </Button>
          </DialogFooter>
          {assessError && <AiServiceNotice error={assessError} onRetry={startAssessment} retrying={generatingAssess} className="mt-2" />}
        </DialogContent>
      </Dialog>
    </StudentLayout>
  );
}

function StudentClassNotesModal({ lessonId, lessonTitle, onClose }) {
  const [notes, setNotes] = useState(undefined); // undefined=loading, null=none

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/student/class-notes", { params: { lesson_id: lessonId } });
        setNotes(data.notes || null);
      } catch { setNotes(null); }
    })();
  }, [lessonId]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="student-class-notes-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-sky-500" /> Class Notes — {lessonTitle}
          </DialogTitle>
          <DialogDescription>Exercise questions and answers for this lesson, shared by your teacher.</DialogDescription>
        </DialogHeader>

        {notes === undefined ? (
          <div className="py-12 text-center text-slate-500" data-testid="student-notes-loading">Loading…</div>
        ) : !notes || !notes.items?.length ? (
          <div className="py-12 text-center" data-testid="student-notes-empty">
            <ClipboardList className="h-8 w-8 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
            <div className="text-slate-900 dark:text-slate-100 font-medium">No class notes yet</div>
            <div className="text-slate-500 text-sm mt-1">Your teacher hasn't published notes for this lesson yet.</div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="student-notes-list">
            {notes.items.map((it, i) => (
              <div key={i} className="rounded-xl border border-slate-200 dark:border-slate-700 p-4" data-testid={`student-note-item-${i}`}>
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 flex gap-2">
                  <span className="text-sky-500">{i + 1}.</span><span className="whitespace-pre-wrap">{it.question}</span>
                </div>
                <div className="text-sm text-slate-700 dark:text-slate-300 mt-2 leading-relaxed pl-5">
                  <MarkdownMessage content={it.answer} />
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NotesSearchModal({ onClose }) {
  const [all, setAll] = useState(undefined); // undefined=loading
  const [subjects, setSubjects] = useState([]);
  const [q, setQ] = useState("");
  const [subject, setSubject] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/student/class-notes/all");
        setAll(data.items || []);
        setSubjects(data.subjects || []);
      } catch { setAll([]); }
    })();
  }, []);

  const results = useMemo(() => {
    if (!all) return [];
    const term = q.trim().toLowerCase();
    return all.filter((it) => {
      if (subject !== "all" && it.subject !== subject) return false;
      if (!term) return true;
      return (it.question + " " + it.answer + " " + it.lesson).toLowerCase().includes(term);
    });
  }, [all, q, subject]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="notes-search-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Search className="h-5 w-5 text-sky-500" /> Search Class Notes
          </DialogTitle>
          <DialogDescription>Search across every lesson's published class notes for your class.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions or answers…" className="rounded-lg" autoFocus data-testid="notes-search-input" />
          {subjects.length > 1 && (
            <Select value={subject} onValueChange={setSubject}>
              <SelectTrigger className="h-10 rounded-lg sm:w-44" data-testid="notes-search-subject"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All subjects</SelectItem>
                {subjects.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        {all === undefined ? (
          <div className="py-12 text-center text-slate-500" data-testid="notes-search-loading">Loading…</div>
        ) : all.length === 0 ? (
          <div className="py-12 text-center text-slate-500" data-testid="notes-search-empty-all">No class notes have been published for your class yet.</div>
        ) : (
          <>
            <div className="text-xs text-slate-400">{results.length} result{results.length === 1 ? "" : "s"}</div>
            <div className="space-y-3" data-testid="notes-search-results">
              {results.length === 0 ? (
                <div className="py-8 text-center text-slate-400 text-sm" data-testid="notes-search-noresult">No notes match "{q}".</div>
              ) : results.map((it, i) => (
                <div key={i} className="rounded-xl border border-slate-200 dark:border-slate-700 p-4" data-testid={`notes-search-item-${i}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-1.5 text-[11px]">
                    <span className="px-2 py-0.5 rounded-full bg-sky-50 dark:bg-sky-900/40 text-sky-600 dark:text-sky-300 font-medium">{it.subject}</span>
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">{it.lesson}</span>
                  </div>
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 whitespace-pre-wrap">{it.question}</div>
                  <div className="text-sm text-slate-700 dark:text-slate-300 mt-1 whitespace-pre-wrap leading-relaxed">{it.answer}</div>
                </div>
              ))}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
